import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { ThemeEvents } from '@theme/events';
import {
  buildDiscountCodeList,
  clearFreeProcessingDiscount,
  getPrimaryDiscountCodeFromUrl,
  isAdditiveFreeProcessingDiscountCode,
  reconcileFreeProcessingDiscount,
} from '@theme/free-processing-discount';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Promise<boolean> | null} */
  #defaultDiscountSyncPromise = null;

  /** @type {string | null} */
  #defaultDiscountSyncCode = null;

  #debouncedOnCartUpdate = debounce(this.#setDiscounts, 500).bind(this);

  async connectedCallback() {
    super.connectedCallback();

    try {
      await this.#setDiscounts(null, true);
      await this.#autoDiscounts();

      const storedDiscount = this.#readSession(Theme.legacyBox.discounts.sessionKey);
      const shouldSyncDefault =
        !storedDiscount || ['auto_apply', 'override_applied'].includes(storedDiscount.applied);

      if (shouldSyncDefault) {
        const fallbackCode =
          storedDiscount?.applied === 'auto_apply' && storedDiscount.code
            ? storedDiscount.code
            : Theme.legacyBox.discounts.autoApply.code;
        const autoApplyCode = await this.#resolveAutoApplyCode(fallbackCode, !storedDiscount);

        if (await this.#syncAutoApplyDefaultDiscount(autoApplyCode)) {
          document.dispatchEvent(new DiscountUpdateEvent({}, this.id));
        }
      } else {
        this.#clearAutoApplyDefaultDiscount();
      }
    } catch (error) {
      console.error(error);
    } finally {
      document.body.classList.remove('discounts-loading');
      document.addEventListener(ThemeEvents.cartUpdate, this.#debouncedOnCartUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    setTimeout(async () => {
      await this.#autoDiscounts();
      document.dispatchEvent(new DiscountUpdateEvent({}, this.id));
      document.removeEventListener(ThemeEvents.cartUpdate, this.#debouncedOnCartUpdate);
    }, 500);
  }

  /**
   * @param {Event | null} event
   * @param {Boolean | null} justInitialized
   */
  async #setDiscounts(event, justInitialized) {
    const existingDiscounts = this.#existingDiscounts();
    const rawStorage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);
    const storedDiscount = this.#readSession(Theme.legacyBox.discounts.sessionKey);
    const storage = storedDiscount ? rawStorage : null;
    const discountsData = storedDiscount || {};
    const storedCode = this.#normalizeDiscountCode(discountsData.code);

    const isOverrideDiscountApplied = Theme.legacyBox.discounts.overrides.find(
      (o) => !o.additive && this.#normalizeDiscountCode(o.auto_discount) === storedCode
    );
    const override = Theme.legacyBox.discounts.overrides.find(
      (o) =>
        this.#isOverrideDiscountActive(o, discountsData) &&
        Theme.legacyBox.cart.items.find((i) => o.variant_id.includes(`${i.id}`))
    );
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(window.location.search);
    const urlCode = this.#getUrlDiscountValue();

    if (urlCode) {
      const type = urlParams.get('lb_discount_type') || 'url_query';
      const normalizedUrlCode = this.#normalizeDiscountCode(urlCode);
      const requestedDiscounts = buildDiscountCodeList(urlCode);
      const normalizedExistingDiscounts = existingDiscounts
        .map((code) => this.#normalizeDiscountCode(code))
        .filter(Boolean);
      const isUrlDiscountApplied = requestedDiscounts.every((code) =>
        normalizedExistingDiscounts.includes(this.#normalizeDiscountCode(code))
      );

      let requestHandled = true;
      if (!isUrlDiscountApplied) {
        requestHandled = await this.applyDiscount(null, urlCode, type);
      } else if (storedCode !== normalizedUrlCode) {
        await this.#persistPrimaryDiscount(urlCode, type);
      }

      if (requestHandled) {
        url.searchParams.delete('lb_discount');
        url.searchParams.delete('lb_discount_type');
        url.searchParams.delete('discount');
        window.history.replaceState({}, '', url);
      }

      return;
    }

    const existingPrimaryDiscount = this.#getPrimaryDiscountCode(existingDiscounts);
    const storedPrimaryCode =
      storedCode && !isAdditiveFreeProcessingDiscountCode(storedCode) ? storedCode : null;
    const primaryCode = existingPrimaryDiscount || storedPrimaryCode;
    const expectedDiscounts = buildDiscountCodeList(primaryCode);
    const normalizedExistingDiscounts = existingDiscounts
      .map((code) => this.#normalizeDiscountCode(code))
      .filter(Boolean);
    const hasMissingDiscount = expectedDiscounts.some(
      (code) => !normalizedExistingDiscounts.includes(this.#normalizeDiscountCode(code))
    );

    if (hasMissingDiscount && (primaryCode || !Theme.legacyBox.discounts.autoApply.active)) {
      const requestedCode = primaryCode || expectedDiscounts[0];
      const primaryType = !primaryCode
        ? 'campaign'
        : existingPrimaryDiscount && existingPrimaryDiscount !== storedCode
          ? 'locally_applied'
          : discountsData.applied || 'locally_applied';
      await this.applyDiscount(null, requestedCode, primaryType);
      return;
    }

    if (existingPrimaryDiscount && storedCode && existingPrimaryDiscount !== storedCode) {
      await this.#persistPrimaryDiscount(existingPrimaryDiscount, 'locally_applied');
      return;
    }

    // If product discount override discount is applied and override product in the cart or component just ininitialized (the same as product just added to the cart),
    // then apply product discount override.
    if (
      override &&
      ((event && event.detail?.data?.source === 'product-form-component') || justInitialized) &&
      discountsData.applied !== 'groupon'
    ) {
      document.body.classList.add('discounts-override-applying');
      await this.applyDiscount(null, override.auto_discount, 'override_applied');
      document.body.classList.remove('discounts-override-applying');
      return;
    }

    // If product discount override discount is applied and no override product in the cart,
    // then apply auto discount or remove discount at all.
    if (!override && isOverrideDiscountApplied && Theme.legacyBox.cart.item_count !== 0) {
      document.body.classList.add('discounts-override-applying');

      const autoApplyCode = await this.#resolveAutoApplyCode(
        Theme.legacyBox.discounts.autoApply.code
      );

      if (autoApplyCode) {
        await this.applyDiscount(null, autoApplyCode, 'auto_apply');
      } else if (existingDiscounts[0]) {
        await this.removeDiscount(null, existingDiscounts[0]);
      }

      document.body.classList.remove('discounts-override-applying');
      return;
    }

    // If no any discounts applied and there is saved data in the storage,
    // then clear storage.
    if (!existingDiscounts.length && storage) {
      sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
      document.dispatchEvent(new DiscountUpdateEvent({}, this.id));
      return;
    }

    // If no saved data in the storage and there is applied discount and any item in the cart exists,
    // then get applied discount data.
    if (!storage && existingPrimaryDiscount && Theme.legacyBox.cart.item_count !== 0) {
      await this.#persistPrimaryDiscount(existingPrimaryDiscount, 'locally_applied');
      document.dispatchEvent(new DiscountUpdateEvent({}, this.id));
    }

    // If saved data exists and auto applied discount code isn't equal to settings,
    // then remove applied discount code and apply the correct one.
    if (storage && discountsData.applied === 'auto_apply') {
      const currentAutoApplyCode = this.#normalizeDiscountCode(discountsData.code);
      const fallbackCode = currentAutoApplyCode || Theme.legacyBox.discounts.autoApply.code;
      const autoApplyCode = await this.#resolveAutoApplyCode(fallbackCode);

      if (autoApplyCode && currentAutoApplyCode && currentAutoApplyCode !== autoApplyCode) {
        await this.removeDiscount(null, discountsData.code);
        await this.applyDiscount(null, autoApplyCode, 'auto_apply');
      }
    }
  }

  /**
   * @param {unknown} code
   * @returns {string | null}
   */
  #normalizeDiscountCode(code) {
    return typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;
  }

  /**
   * @param {string[]} codes
   * @returns {string | null}
   */
  #getPrimaryDiscountCode(codes) {
    for (const code of codes) {
      const normalizedCode = this.#normalizeDiscountCode(code);

      if (normalizedCode && !isAdditiveFreeProcessingDiscountCode(normalizedCode)) {
        return normalizedCode;
      }
    }

    return null;
  }

  /**
   * @returns {string | null}
   */
  #getUrlDiscountValue() {
    return getPrimaryDiscountCodeFromUrl();
  }

  /**
   * @returns {string | null}
   */
  #getUrlDiscountCode() {
    return this.#normalizeDiscountCode(this.#getUrlDiscountValue());
  }

  /**
   * @param {any} override
   * @param {any} discountsData
   */
  #isOverrideDiscountActive(override, discountsData) {
    if (override?.additive) return false;
    if (!override?.is_bogo) return true;

    const overrideCode = this.#normalizeDiscountCode(override?.auto_discount);

    if (!overrideCode) return false;

    return [this.#getUrlDiscountCode(), this.#normalizeDiscountCode(discountsData?.code)].includes(
      overrideCode
    );
  }

  async #autoDiscounts() {
    const storage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);

    if (storage) return;

    const type = this.#determineDiscountApplication();

    if (!Theme.legacyBox.discounts.allow || !type) return;

    switch (type) {
      case 'url_query': {
        const url = new URL(window.location.href);
        const code = this.#getUrlDiscountValue();

        if (code) {
          const requestHandled = await this.applyDiscount(null, code, type);

          if (requestHandled) {
            url.searchParams.delete('lb_discount');
            url.searchParams.delete('discount');
            window.history.replaceState({}, '', url);
          }
        }

        break;
      }
      case 'auto_apply': {
        const code = await this.#resolveAutoApplyCode(
          Theme.legacyBox.discounts.autoApply.code,
          true
        );

        const applied = code ? await this.applyDiscount(null, code, type) : false;
        const persistedCode = this.#normalizeDiscountCode(
          this.#readSession(Theme.legacyBox.discounts.sessionKey)?.code
        );
        const primaryApplied =
          code && persistedCode === this.#normalizeDiscountCode(code) && applied;

        if (!primaryApplied) {
          const [standaloneAdditiveCode] = buildDiscountCodeList(null);
          if (
            standaloneAdditiveCode &&
            !isAdditiveFreeProcessingDiscountCode(code) &&
            this.#normalizeDiscountCode(standaloneAdditiveCode) !==
              this.#normalizeDiscountCode(code)
          ) {
            await this.applyDiscount(null, standaloneAdditiveCode, 'campaign');
          }
        }

        break;
      }
      default:
        break;
    }
  }

  #determineDiscountApplication() {
    const storage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);

    if (storage) {
      try {
        const data = JSON.parse(storage);

        if (data?.applied) return data.applied;
      } catch (e) {}
    }

    const urlValue = this.#getUrlDiscountValue();
    if (urlValue) return 'url_query';

    if (Theme.legacyBox.discounts.autoApply.active) {
      return 'auto_apply';
    }

    return null;
  }

  /**
   * @param {string} key
   */
  #readSession(key) {
    if (!key) return null;

    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) return null;

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  /**
   * Persists a primary code only after Shopify has confirmed it in the cart. Worker metadata is
   * optional; a minimal record prevents a transient worker failure from restoring a stale code.
   *
   * @param {string} code
   * @param {string} type
   * @param {AbortSignal} [signal]
   */
  async #persistPrimaryDiscount(code, type, signal) {
    const normalizedCode = this.#normalizeDiscountCode(code);
    if (!normalizedCode) return false;

    let discountData;
    try {
      discountData = await this.#getDiscountData(normalizedCode, signal);
    } catch (error) {
      if (signal?.aborted) return false;
      console.error(error);
    }

    const hasWorkerData = discountData?.status === 'success';
    if (!hasWorkerData) {
      discountData = { code: normalizedCode };
    }

    const sessionData = JSON.stringify({
      applied: type,
      ...discountData,
      ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(normalizedCode, type) || {}),
    });

    sessionStorage.setItem(Theme.legacyBox.discounts.sessionKey, sessionData);
    if (type === 'auto_apply') {
      if (hasWorkerData) {
        sessionStorage.setItem(Theme.legacyBox.discounts.defaultSessionKey, sessionData);
      } else {
        this.#clearAutoApplyDefaultDiscount();
      }
    }

    return true;
  }

  #clearAutoApplyDefaultDiscount() {
    sessionStorage.removeItem(Theme.legacyBox.discounts.defaultSessionKey);
  }

  /**
   * Resolves the sitewide assignment without allowing an A/B service failure to block the theme's
   * configured fallback discount.
   *
   * @param {string | null} fallbackCode
   * @param {boolean} [triggerAssignment]
   */
  async #resolveAutoApplyCode(fallbackCode, triggerAssignment = false) {
    if (!Theme.legacyBox.discounts.autoApply.active || !fallbackCode) return null;

    try {
      return (
        (await window.Legacybox?.resolveSitewideAutoApplyDiscountCode?.({
          fallbackCode,
          triggerAssignment,
        })) || fallbackCode
      );
    } catch (error) {
      console.error(error);
      return fallbackCode;
    }
  }

  /**
   * Keep product-price.js' default_discount fallback aligned with the active
   * auto-apply code. This prevents old sitewide A/B assignments from leaving
   * stale strikethrough pricing in session storage.
   *
   * @param {string | null} autoApplyCode
   * @returns {Promise<boolean>}
   */
  async #syncAutoApplyDefaultDiscount(autoApplyCode) {
    if (!Theme.legacyBox.discounts.autoApply.active || !autoApplyCode) {
      this.#clearAutoApplyDefaultDiscount();
      return false;
    }

    const normalizedAutoApplyCode = this.#normalizeDiscountCode(autoApplyCode);

    if (!normalizedAutoApplyCode) {
      this.#clearAutoApplyDefaultDiscount();
      return false;
    }

    const currentDefaultDiscount = this.#readSession(Theme.legacyBox.discounts.defaultSessionKey);
    const currentDefaultCode = this.#normalizeDiscountCode(currentDefaultDiscount?.code);

    if (currentDefaultCode === normalizedAutoApplyCode) return false;

    if (
      this.#defaultDiscountSyncPromise &&
      this.#defaultDiscountSyncCode === normalizedAutoApplyCode
    ) {
      return this.#defaultDiscountSyncPromise;
    }

    this.#defaultDiscountSyncCode = normalizedAutoApplyCode;
    this.#defaultDiscountSyncPromise = (async () => {
      let defaultDiscountData;
      try {
        defaultDiscountData = await this.#getDiscountData(normalizedAutoApplyCode);
      } catch (error) {
        console.error(error);
        this.#clearAutoApplyDefaultDiscount();
        return false;
      }

      if (defaultDiscountData?.status !== 'success') return false;

      sessionStorage.setItem(
        Theme.legacyBox.discounts.defaultSessionKey,
        JSON.stringify({
          ...defaultDiscountData,
          ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(
            normalizedAutoApplyCode,
            'auto_apply'
          ) || {}),
        })
      );

      return true;
    })();

    try {
      return await this.#defaultDiscountSyncPromise;
    } finally {
      if (this.#defaultDiscountSyncCode === normalizedAutoApplyCode) {
        this.#defaultDiscountSyncCode = null;
        this.#defaultDiscountSyncPromise = null;
      }
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent | null} event - The submit event on our form.
   * @param {String} code - Discount code.
   */
  applyDiscount = async (event, code, type = 'locally_applied') => {
    let discountCode;
    let discountCodeValue = code ? code : '';
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      this.refs;

    if (!code) {
      event?.preventDefault();
      event?.stopPropagation();

      const form = event?.target;
      if (!(form instanceof HTMLFormElement)) return;

      discountCode = form.querySelector('input[name="discount"]');
      if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string')
        return;

      discountCodeValue = discountCode.value;
    }

    const abortController = this.#createAbortController();
    const container = this.closest('.cart-discount');

    try {
      const existingDiscounts = this.#existingDiscounts();
      const requestedDiscounts = buildDiscountCodeList(discountCodeValue);
      const requestedCodeIsAdditive = isAdditiveFreeProcessingDiscountCode(discountCodeValue);
      const hasRequestedAdditiveDiscount = requestedDiscounts.some(
        isAdditiveFreeProcessingDiscountCode
      );
      const normalizedExistingDiscounts = existingDiscounts
        .map((existingCode) => this.#normalizeDiscountCode(existingCode))
        .filter(Boolean);

      if (
        requestedDiscounts.length > 0 &&
        requestedDiscounts.every((requestedCode) =>
          normalizedExistingDiscounts.includes(this.#normalizeDiscountCode(requestedCode))
        ) &&
        !Theme.legacyBox.discounts.overrides.some(
          (o) => !o.additive && o.auto_discount.toLowerCase() === discountCodeValue.toLowerCase()
        )
      )
        return true;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      container?.classList.add('is-loading');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: requestedDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error(`Discount update failed (${response.status})`);

      const data = await response.json();

      const normalizedRequestedCode = this.#normalizeDiscountCode(discountCodeValue);
      const hasRequestedLineDiscount = data.items?.some((i) =>
        i.discounts?.some((d) => this.#normalizeDiscountCode(d.title) === normalizedRequestedCode)
      );
      const primaryRejected = data.discount_codes?.some(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
          this.#normalizeDiscountCode(discount.code) === normalizedRequestedCode &&
          discount.applicable === false
      );

      if (primaryRejected && !hasRequestedLineDiscount) {
        if (requestedCodeIsAdditive) {
          reconcileFreeProcessingDiscount(data, { clearRejected: true });
        }

        if (hasRequestedAdditiveDiscount) {
          const previousPrimary = this.#getPrimaryDiscountCode(existingDiscounts);
          const rollbackResponse = await fetch(Theme.routes.cart_update_url, {
            ...fetchConfig('json', {
              body: JSON.stringify({
                discount: existingDiscounts.join(','),
                sections: [this.dataset.sectionId],
              }),
            }),
            signal: abortController.signal,
          });
          if (!rollbackResponse.ok) {
            reconcileFreeProcessingDiscount(data);
            if (previousPrimary && !this.#isDiscountPresent(data, previousPrimary)) {
              sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
            }
            document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

            const currentHtml = data.sections?.[this.dataset.sectionId];
            if (currentHtml) {
              document.body.classList.remove('discounts-override-applying');
              await morphSection(this.dataset.sectionId, currentHtml);
            }

            throw new Error(`Discount rollback failed (${rollbackResponse.status})`);
          }

          const rollbackData = await rollbackResponse.json();
          reconcileFreeProcessingDiscount(rollbackData);

          if (previousPrimary && !this.#isDiscountPresent(rollbackData, previousPrimary)) {
            sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
          }

          document.dispatchEvent(new DiscountUpdateEvent(rollbackData, this.id));
        }

        if (discountCode) discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return true;
      }

      reconcileFreeProcessingDiscount(data);

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];

      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement ? element.dataset.discountCode : null
          )
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find(
            (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return (
                this.#normalizeDiscountCode(discount.code) === normalizedRequestedCode &&
                discount.applicable === true
              );
            }
          ) &&
          !hasRequestedLineDiscount
        ) {
          this.#handleDiscountError('shipping');
          if (discountCode) discountCode.value = '';
          return true;
        }
      }

      if (
        !requestedCodeIsAdditive &&
        !(await this.#persistPrimaryDiscount(discountCodeValue, type, abortController.signal))
      ) {
        return false;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      // The override loading class hides price text; remove it before replacing
      // the cart DOM so morphed markup does not briefly paint as a placeholder.
      document.body.classList.remove('discounts-override-applying');
      await morphSection(this.dataset.sectionId, newHtml);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      if (this.#activeFetch === abortController) {
        container?.classList.remove('is-loading');
        this.#activeFetch = null;
      }

      if (event) cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent | null} event - The mouse or keyboard event in our pill.
   * @param {String} code - Discount code
   */
  removeDiscount = async (event, code) => {
    let discountCode = code;

    if (event) {
      event.preventDefault();
      event.stopPropagation();

      if (
        (event instanceof KeyboardEvent && event.key !== 'Enter') ||
        !(event instanceof MouseEvent) ||
        !(event.target instanceof HTMLElement) ||
        typeof this.dataset.sectionId !== 'string'
      ) {
        return;
      }

      const pill = event.target.closest('.cart-discount__pill');
      if (!(pill instanceof HTMLLIElement)) return;

      discountCode = pill.dataset.discountCode || '';
    }

    if (!discountCode) return;

    const removingAdditiveFreeProcessingDiscount =
      isAdditiveFreeProcessingDiscountCode(discountCode);

    const existingDiscounts = this.#existingDiscounts();
    const normalizedDiscountCode = this.#normalizeDiscountCode(discountCode);
    const index = existingDiscounts.findIndex(
      (existingCode) => this.#normalizeDiscountCode(existingCode) === normalizedDiscountCode
    );
    if (index === -1) return;

    existingDiscounts.splice(index, 1);
    const remainingDiscounts = removingAdditiveFreeProcessingDiscount
      ? existingDiscounts
      : existingDiscounts.filter((existingCode) => {
          return !isAdditiveFreeProcessingDiscountCode(existingCode);
        });

    const abortController = this.#createAbortController();
    const container = this.closest('.cart-discount');
    container?.classList.add('is-loading');

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: remainingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error(`Discount removal failed (${response.status})`);

      const data = await response.json();
      const isDiscountStillPresent = this.#isDiscountPresent(data, normalizedDiscountCode);

      if (!isDiscountStillPresent) {
        if (removingAdditiveFreeProcessingDiscount) {
          clearFreeProcessingDiscount();
        } else {
          sessionStorage.removeItem(Theme.legacyBox.discounts.sessionKey);
        }
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      }

      document.body.classList.remove('discounts-override-applying');
      await morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      console.error(error);
    } finally {
      if (this.#activeFetch === abortController) {
        container?.classList.remove('is-loading');
        this.#activeFetch = null;
      }
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      this.refs;
    const target =
      type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Confirms a code from every discount location returned by Shopify's Cart API.
   *
   * @param {any} cart
   * @param {unknown} code
   */
  #isDiscountPresent(cart, code) {
    const normalizedCode = this.#normalizeDiscountCode(code);
    if (!normalizedCode) return false;

    return Boolean(
      cart?.discount_codes?.some(
        (discount) =>
          this.#normalizeDiscountCode(discount.code) === normalizedCode &&
          discount.applicable !== false
      ) ||
      cart?.cart_level_discount_applications?.some(
        (discount) => this.#normalizeDiscountCode(discount.title) === normalizedCode
      ) ||
      cart?.items?.some((item) =>
        item.discounts?.some(
          (discount) => this.#normalizeDiscountCode(discount.title) === normalizedCode
        )
      )
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = document.querySelector('cart-discount-component')
      ? this.querySelectorAll('.cart-discount__pill')
      : [];

    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Returns discount code information.
   * @param {string} code
   * @param {AbortSignal} [signal]
   */
  async #getDiscountData(code, signal) {
    const response = await fetch(Theme.legacyBox.discounts.worker, {
      method: 'POST',
      body: JSON.stringify({ code }),
      signal,
    });
    if (!response.ok) throw new Error(`Discount lookup failed (${response.status})`);

    const { data } = await response.json();

    return data;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
